import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useContext } from 'react';
import { useSyncExternalStore } from 'use-sync-external-store/shim';

import apiFetch from '@scale/frontend-lib/api-fetch';
import { getTimerLoopIntervalSecs } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import {
  ReactHOC,
  withFR,
  withHocDisplayName,
  withNonReactStatics,
} from '@scale/frontend-lib/hocHelpers';
import { IProjectJson } from '@scale/shared/types/project';
import { getPageLoadSingleton } from 'react-scale/lib/globalSingletons';
import { RemoAdminTimerData } from 'react-scale/components/remoAdmin/remoAdminContext';
import useStorageRef from './tasks/ThreadedInteraction/hooks/useStorageRef';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import _ from 'lodash';

interface V2TimerProps {
  type?: TaskType;
  _id?: string;
  project?: IProjectJson;
  loggedUserId?: string;
  debugMode?: boolean;
  isCorpMode?: boolean;
  isTestQuestion?: boolean;
  isCustomerReviewMode: boolean;
  usePageTracker?: boolean;
  pageType?: string;
  trainingAttemptId?: string;
}

// Debounce function to handle multiple calls to tick
function debounce<T extends (...args: any[]) => any>(func: T, wait: number) {
  let timeout: NodeJS.Timeout;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

export type TimerData = {
  startTime: number;
  lastTime: number;
  totalTime: number;
  serverTimeSpent: number;
};

type TimerContextValue = {
  getTimerData: () => TimerData;
  setTimerData: Dispatch<SetStateAction<TimerData>>;
  subscribeTimerData: (callback: any) => () => boolean;
};

const TimerContext = React.createContext<TimerContextValue>({} as TimerContextValue);

const DEFAULT_COURSE_ACTIVE_TIME_SECS = 120;

export const useTimer = () => {
  const timerContext = useContext(TimerContext) as Partial<TimerContextValue>;
  const [getTimerData, setTimerData, subscribeTimerData] = useStorageRef<TimerData>(
    'timer',
    buildDefaultTimerData(),
  );

  const timer = {
    getTimerData,
    setTimerData,
    subscribeTimerData,
  };
  return timerContext.getTimerData ? (timerContext as TimerContextValue) : timer;
};

const buildDefaultTimerData = () => ({
  startTime: Date.now(),
  lastTime: Date.now(),
  totalTime: 0,
  serverTimeSpent: 0,
});

export const useTimerData = (): [TimerData, Dispatch<SetStateAction<TimerData>>] => {
  const { getTimerData, setTimerData, subscribeTimerData } = useTimer();
  const timerData = useSyncExternalStore(subscribeTimerData, getTimerData);
  return [timerData, setTimerData];
};

const withV2Timer: ReactHOC<{ ref?: any } & any, V2TimerProps & any> = WrappedComponent =>
  withFR(
    withNonReactStatics(WrappedComponent)(
      withHocDisplayName('withV2Timer')((props: V2TimerProps & any) => {
        const [getTimerData, setTimerData, subscribeTimerData] = useStorageRef<TimerData>(
          'timer',
          buildDefaultTimerData(),
        );
        if (props.timerDisabled) {
          return (
            <TimerContext.Provider
              value={{
                getTimerData,
                setTimerData,
                subscribeTimerData,
              }}
            >
              <WrappedComponent ref={props.forwardedRef} {...props} />
            </TimerContext.Provider>
          );
        }
        const enableTimerForTrainingAttempt = useUnleashFeatureFlag(
          'enable-timer-for-training-attempt',
          {},
          false,
        );
        const updateTimeFrequency =
          getTimerLoopIntervalSecs(props.type, props.usePageTracker, !_.isNil(props.courseId)) *
          1000;
        const shouldPingPageTracker = props.usePageTracker;
        const shouldPingForTrainingAttempt =
          enableTimerForTrainingAttempt && props.trainingAttemptId;
        const shouldPingTasks =
          props.loggedUserId &&
          !props.debugMode &&
          !props.isCorpMode &&
          (!props.isTestQuestion || props.isScaleStudio) &&
          !props.isCustomerReviewMode;

        const isCoursePage = Boolean(props.courseId) && !props.training?.isLoading;

        const shouldPing =
          shouldPingForTrainingAttempt || shouldPingTasks || shouldPingPageTracker || isCoursePage;

        const params =
          typeof window !== 'undefined'
            ? new URLSearchParams(window.location.search)
            : new URLSearchParams();
        const qaOperationId = params.get('qaOperationId') ?? undefined;

        const lastActive = useRef(new Date());
        const moveListener = useCallback(() => {
          lastActive.current = new Date();
        }, []);

        const activeTimeFrequency = !_.isNil(props.courseId)
          ? DEFAULT_COURSE_ACTIVE_TIME_SECS * 1000
          : updateTimeFrequency; // 2 minutes for course, otherwise remain same

        const active = () => {
          if (!lastActive.current) {
            return false;
          }
          return Date.now() - lastActive.current.getTime() < activeTimeFrequency;
        };

        const pageSession = getPageLoadSingleton();

        const tick = useCallback(async () => {
          const totalTime = getTimerData();
          const shouldReset = new Date(totalTime.startTime).getDay() !== new Date().getDay();
          setTimerData({
            startTime: shouldReset ? Date.now() : totalTime.startTime,
            lastTime: Date.now(),
            totalTime: totalTime.totalTime + Date.now() - totalTime.lastTime,
            serverTimeSpent: totalTime.serverTimeSpent,
          });
          if (!shouldPing) return;

          let tickId;
          let type;
          if (shouldPingPageTracker) {
            tickId = pageSession;
            type = 'page';
          } else if (isCoursePage) {
            tickId = props.courseId;
            type = 'course';
          } else if (shouldPingForTrainingAttempt) {
            tickId = props.trainingAttemptId;
            type = 'tick';
          } else {
            tickId = props._id;
            type = 'tick';
          }
          if (props.isScaleStudio && props.isTestQuestion) {
            tickId = props.trainingAttemptId;
          }
          if (!tickId) return;

          const endpoint = `internal/timer/${type}/${tickId}`;
          const body: any = { active: active() };

          if (shouldPingPageTracker) {
            body.pageType = props.pageType;
          }

          try {
            const response = await apiFetch(null, endpoint, {
              method: 'PUT',
              body,
              query: {
                project: RemoAdminTimerData.projectId,
                auditBatchId: RemoAdminTimerData.auditBatchId,
                pageUrl: window.location.pathname,
                qaOperationId,
              },
            });

            if (type === 'tick') {
              const { timeSpent } = response;
              setTimerData({
                ...totalTime,
                serverTimeSpent: Number(timeSpent),
              });
            }
          } catch {
            // noop
          }

          if (props.childIds) {
            // tick child tasks as well. Then in handleSubmissionWorkflow.
            for (const childId of props.childIds) {
              if (childId) {
                apiFetch(null, `internal/timer/${type}/${childId}`, {
                  method: 'PUT',
                  body,
                  query: {
                    project: RemoAdminTimerData.projectId,
                    auditBatchId: RemoAdminTimerData.auditBatchId,
                    pageUrl: window.location.pathname,
                    qaOperationId,
                  },
                });
              }
            }
          }
        }, [
          shouldPing,
          props._id,
          props.pageType,
          pageSession,
          qaOperationId,
          getTimerData,
          setTimerData,
        ]);

        // Debounced version of tick
        const cleanupDebouncedTick = useCallback(debounce(tick, updateTimeFrequency), [
          tick,
          updateTimeFrequency,
        ]);

        useEffect(() => {
          if (!shouldPing) return;

          const handleBeforeUnload = () => {
            cleanupDebouncedTick();
            return null;
          };

          window.addEventListener('mousemove', moveListener);
          window.addEventListener('keydown', moveListener);
          // Should we block while the request is sent?  Seems rude
          // In practice, the request will probably go through because we also have a "are you sure" popup
          window.addEventListener('beforeunload', handleBeforeUnload);
          tick(); // ensures at least one call to tick
          const updateTimeSpentLoopId = window.setInterval(tick, updateTimeFrequency);
          return () => {
            window.removeEventListener('mousemove', moveListener);
            window.removeEventListener('keydown', moveListener);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            cleanupDebouncedTick();
            clearInterval(updateTimeSpentLoopId);
          };
        }, [tick, cleanupDebouncedTick, moveListener, shouldPing, updateTimeFrequency]);

        return (
          <TimerContext.Provider
            value={{
              getTimerData,
              setTimerData,
              subscribeTimerData,
            }}
          >
            <WrappedComponent ref={props.forwardedRef} {...props} />
          </TimerContext.Provider>
        );
      }),
    ),
  );

export default withV2Timer;
